import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject } from 'rxjs';

export interface SupportedLanguage {
  code: string;
  label: string;
  nativeLabel: string;
}

export const SA_LANGUAGES: SupportedLanguage[] = [
  { code: 'en',  label: 'English',         nativeLabel: 'English'      },
  { code: 'zu',  label: 'Zulu',            nativeLabel: 'isiZulu'      },
  { code: 'xh',  label: 'Xhosa',           nativeLabel: 'isiXhosa'     },
  { code: 'af',  label: 'Afrikaans',       nativeLabel: 'Afrikaans'    },
  { code: 'nso', label: 'Northern Sotho',  nativeLabel: 'Sepedi'       },
  { code: 'tn',  label: 'Tswana',          nativeLabel: 'Setswana'     },
  { code: 'st',  label: 'Sotho',           nativeLabel: 'Sesotho'      },
  { code: 'ts',  label: 'Tsonga',          nativeLabel: 'Xitsonga'     },
  { code: 'ss',  label: 'Swati',           nativeLabel: 'siSwati'      },
  { code: 've',  label: 'Venda',           nativeLabel: 'Tshivenda'    },
  { code: 'nr',  label: 'Ndebele',         nativeLabel: 'isiNdebele'   },
];

const SUPPORTED_CODES = SA_LANGUAGES.map(l => l.code);
const PREFERENCE_KEY = '_appLanguage';
/** localStorage key for the synchronous mirror of the language preference. */
const SYNC_PREF_KEY = '_appLangSync';
const DEFAULT_LANG = 'en';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly supportedLanguages = SA_LANGUAGES;

  private readonly _activeLanguage$ = new BehaviorSubject<string>(DEFAULT_LANG);
  /** Observable of the currently active BCP 47 language code. */
  readonly activeLanguage$ = this._activeLanguage$.asObservable();

  constructor(private readonly translate: TranslateService) {}

  /**
   * Initialise translations synchronously — no awaiting a native bridge or
   * network call. Returns immediately so AppComponent.ngOnInit does not stall.
   *
   * Priority: localStorage mirror → device locale → 'en'
   *
   * After returning, Capacitor Preferences is reconciled in the background.
   * If the durable native value differs from the sync mirror (e.g. after a
   * browser-storage clear), the language is silently switched.
   */
  async initialize(): Promise<void> {
    this.translate.addLangs(SUPPORTED_CODES);
    this.translate.setDefaultLang(DEFAULT_LANG);

    // Read synchronously — localStorage is in-process, zero latency.
    const syncStored = this.readSyncPreference();
    const resolved = syncStored ?? this.detectDeviceLanguage();
    // CdnTranslateLoader.getTranslation() returns of(bundled) synchronously,
    // so translate.use() resolves in the same tick — no network wait.
    this.applyLanguage(resolved);

    // Reconcile with the durable native store in the background.
    // We do NOT await this — the UI is already rendered with the correct language.
    this.reconcilePreferencesAsync();
  }

  /** Returns the currently active language code. */
  get currentLanguage(): string {
    return this._activeLanguage$.value;
  }

  /**
   * True on first ever launch before the user has explicitly chosen a language.
   * Used to decide whether to show the language-setup onboarding screen.
   */
  get isFirstLaunch(): boolean {
    try {
      return localStorage.getItem(SYNC_PREF_KEY) === null;
    } catch {
      return false;
    }
  }

  /** Persist and apply a new language selection. */
  async setLanguage(code: string): Promise<void> {
    const safe = SUPPORTED_CODES.includes(code) ? code : DEFAULT_LANG;
    // Sync mirror first — available immediately on next cold start.
    this.writeSyncPreference(safe);
    // Durable native storage (async — native bridge call).
    await Preferences.set({ key: PREFERENCE_KEY, value: safe });
    this.applyLanguage(safe);
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  /**
   * Apply a language code synchronously.
   * Because getTranslation() returns of(bundled), translate.use() resolves
   * in the same JS tick — subscribe() fires synchronously.
   */
  private applyLanguage(code: string): void {
    this.translate.use(code).subscribe();
    this._activeLanguage$.next(code);
    document.documentElement.lang = code;
  }

  private readSyncPreference(): string | null {
    try {
      const v = localStorage.getItem(SYNC_PREF_KEY);
      return v && SUPPORTED_CODES.includes(v) ? v : null;
    } catch {
      return null;
    }
  }

  private writeSyncPreference(code: string): void {
    try {
      localStorage.setItem(SYNC_PREF_KEY, code);
    } catch {
      // localStorage unavailable — not fatal; device locale is the fallback.
    }
  }

  /**
   * Fire-and-forget: read from Capacitor Preferences (async native bridge).
   * If the value differs from what is active, switch silently.
   * This covers the edge case where localStorage was cleared but native
   * Preferences survived (e.g. web-storage wipe without app reinstall).
   */
  private async reconcilePreferencesAsync(): Promise<void> {
    try {
      const { value } = await Preferences.get({ key: PREFERENCE_KEY });
      const stored = value && SUPPORTED_CODES.includes(value) ? value : null;
      if (stored && stored !== this._activeLanguage$.value) {
        await this.setLanguage(stored);
      }
    } catch {
      // Native bridge failure — the sync localStorage value is already applied.
    }
  }

  /**
   * Detect language from browser/device locale.
   * navigator.language returns e.g. "zu-ZA", "en-US", "af".
   * We match on the primary subtag first, then fall back to 'en'.
   */
  private detectDeviceLanguage(): string {
    const deviceLocale = (navigator.language ?? '').toLowerCase();
    // Try exact match first (e.g. "nso")
    if (SUPPORTED_CODES.includes(deviceLocale)) {
      return deviceLocale;
    }
    // Try primary subtag (e.g. "zu" from "zu-ZA")
    const primary = deviceLocale.split('-')[0];
    return SUPPORTED_CODES.includes(primary) ? primary : DEFAULT_LANG;
  }
}
